import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { PlatformShell } from '../shell/PlatformShell';
import { AFFILPLATFORM_MENU, type MenuItem, type SubItem } from '../shell/platform-menu';
import { MailingDashboardPage } from '../mailing/MailingDashboardPage';
import { MailingKampanePage } from '../mailing/MailingKampanePage';
import { MailingSablonyPage } from '../mailing/MailingSablonyPage';
import { AffilmasterMailingSegmentyPage } from '../affilmaster/AffilmasterMailingSegmentyPage';
import { SettingsPage } from '../settings/SettingsPage';
import { TasksPage } from '../tasks/TasksPage';
import { HelpdeskPage } from '../helpdesk/HelpdeskPage';
import { CrmLeadyPage } from '../crm/CrmLeadyPage';
import { CrmPipelinePage } from '../crm/CrmPipelinePage';
import { CrmFormularPage } from '../crm/CrmFormularPage';
import { KlientiPage } from '../klienti/KlientiPage';
import { lazy, Suspense } from 'react';
import { setPagesSpace } from '../api/pages';
import { useScrollRestore } from '../shell/useScrollRestore';
import { SpaceDashboard } from '../shell/SpaceDashboard';
import { ArcPage, ArcPageHeader, ArcPanel } from '../components/arc/primitives/arc-primitives';

const StrankyPodporaPage = lazy(() => import('../affilmaster/stranky/StrankyPodporaPage').then((m) => ({ default: m.StrankyPodporaPage })));
const StrankyB2BPage = lazy(() => import('../affilmaster/stranky/StrankyB2BPage').then((m) => ({ default: m.StrankyB2BPage })));
const StrankyVizitkyPage = lazy(() => import('../affilmaster/stranky/StrankyVizitkyPage').then((m) => ({ default: m.StrankyVizitkyPage })));
import '../crm/crm-redesign.css';

type Route = { moduleId: string; childId?: string; detail?: string };

function routeToPath(route: Route): string {
  if (route.moduleId === 'settings') return '/nastaveni';
  if (route.moduleId === 'tasks') return '/ukoly';
  if (route.moduleId === 'helpdesk') return '/helpdesk';
  if (route.moduleId === 'dashboard') return '/';
  if (route.childId) {
    const mod = AFFILPLATFORM_MENU.find((m) => m.id === route.moduleId);
    const child = mod?.children?.find((c) => c.id === route.childId);
    if (child?.path) return route.detail ? `${child.path}/${route.detail}` : child.path;
  }
  return '/';
}

const AP_MAILING_COUNTRIES = ['CZ', 'SK'] as const;

function pathToRoute(pathname: string): Route {
  if (pathname === '/nastaveni') return { moduleId: 'settings' };
  if (pathname === '/ukoly') return { moduleId: 'tasks' };
  if (pathname === '/helpdesk') return { moduleId: 'helpdesk' };
  if (pathname === '/' || pathname === '') return { moduleId: 'dashboard' };
  for (const mod of AFFILPLATFORM_MENU) {
    for (const child of mod.children ?? []) {
      if (!child.path) continue;
      if (pathname === child.path) return { moduleId: mod.id, childId: child.id };
      if (pathname.startsWith(child.path + '/')) {
        const remainder = pathname.slice(child.path.length + 1);
        const detail = decodeURIComponent(child.id === 'ap-vystupy-b2b' ? remainder : remainder.split('/')[0] ?? '');
        return { moduleId: mod.id, childId: child.id, detail: detail || undefined };
      }
    }
  }
  return { moduleId: 'dashboard' };
}

export function AffilplatformShell() {
  setPagesSpace('affilplatform');
  const { me } = useAuth();
  const [route, setRoute] = useState<Route>(() => pathToRoute(window.location.pathname));
  const readTaskId = (): number | null => {
    const raw = new URLSearchParams(window.location.search).get('taskId');
    return raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null;
  };
  const [taskDetailId, setTaskDetailId] = useState<number | null>(readTaskId);

  useEffect(() => {
    const base = routeToPath(route);
    const q = route.moduleId === 'tasks' && taskDetailId != null ? `?taskId=${taskDetailId}` : '';
    const target = base + q;
    if (target !== window.location.pathname + window.location.search) {
      window.history.pushState(null, '', target);
    }
  }, [route, taskDetailId]);

  useEffect(() => {
    const onPop = () => { setRoute(pathToRoute(window.location.pathname)); setTaskDetailId(readTaskId()); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useScrollRestore(`${routeToPath(route)}|${taskDetailId ?? ''}`);

  if (!me) return null;

  const selectModule = (item: MenuItem) => {
    const first = item.children?.[0];
    setRoute(first ? { moduleId: item.id, childId: first.id } : { moduleId: item.id });
  };
  const selectChild = (item: MenuItem, child: SubItem) => {
    setRoute({ moduleId: item.id, childId: child.id });
  };

  const module = AFFILPLATFORM_MENU.find((m) => m.id === route.moduleId) ?? null;
  const child = module?.children?.find((c) => c.id === route.childId) ?? null;
  const isTasksActive = route.moduleId === 'tasks';
  const isHelpdeskActive = route.moduleId === 'helpdesk';
  const isChrome = route.moduleId === 'settings' || isTasksActive || isHelpdeskActive;
  const pageTitle =
    route.moduleId === 'dashboard'
      ? 'Dashboard'
      : module && child
        ? `${module.label} - ${child.label}`
        : (module?.label ?? '');

  return (
    <PlatformShell
      arcRefresh
      menu={AFFILPLATFORM_MENU}
      currentSpaceSlug="affilplatform"
      activeModuleId={route.moduleId}
      activeChildId={route.childId}
      isTasksActive={isTasksActive}
      isHelpdeskActive={isHelpdeskActive}
      showTasks
      showHelpdesk
      showCrony={false}
      onSelectModule={selectModule}
      onSelectChild={selectChild}
      onOpenTasks={() => { setTaskDetailId(null); setRoute({ moduleId: 'tasks' }); }}
      onOpenTask={(taskId) => { setTaskDetailId(taskId); setRoute({ moduleId: 'tasks' }); }}
      onOpenHelpdesk={() => setRoute({ moduleId: 'helpdesk' })}
      onOpenSettings={() => setRoute({ moduleId: 'settings' })}
      pageTitle={isChrome ? undefined : pageTitle}
    >
      {route.moduleId === 'settings' ? (
        <SettingsPage />
      ) : isTasksActive ? (
        <TasksPage selectedTaskId={taskDetailId} onSelectTask={setTaskDetailId} />
      ) : isHelpdeskActive ? (
        <HelpdeskPage lockedSpaceId={me.activeSpaceId ?? undefined} />
      ) : route.moduleId === 'dashboard' ? (
        <AffilplatformDashboard onOpenLeady={() => setRoute({ moduleId: 'crm', childId: 'crm-leady' })} />
      ) : route.childId === 'ap-klienti-seznam' ? (
        <KlientiPage
          space="affilplatform"
          detailId={route.detail}
          onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
          onOpenLead={(leadId) => setRoute({ moduleId: 'crm', childId: 'crm-leady', detail: String(leadId) })}
        />
      ) : route.childId === 'crm-leady' ? (
        <AffilplatformPage
          title={route.detail === 'statistiky' ? 'Statistiky leadů' : 'Leady'}
          description={route.detail === 'statistiky' ? 'Vývoj příležitostí, úspěšnost a měsíční hodnota pipeline.' : 'Přehled leadů a obchodních příležitostí'}
          actionsSlotId={route.detail && route.detail !== 'novy' ? undefined : 'crm-leady-actions'}
        >
        <CrmLeadyPage
          actionsSlotId={route.detail && route.detail !== 'novy' ? undefined : 'crm-leady-actions'}
          detailId={route.detail}
          onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
          onOpenClient={(_name, partnerId) => setRoute({ moduleId: 'klienti', childId: 'ap-klienti-seznam', detail: String(partnerId) })}
        />
        </AffilplatformPage>
      ) : route.childId === 'crm-pipeline' ? (
        <AffilplatformPage title="Pipeline" description="Obchodní fáze leadů">
          <CrmPipelinePage onOpenDetail={(id) => setRoute({ moduleId: 'crm', childId: 'crm-leady', detail: id })} />
        </AffilplatformPage>
      ) : route.childId === 'crm-formular' ? (
        <AffilplatformPage title="Formulář" description="Nastavení veřejného formuláře pro poptávky">
          <CrmFormularPage hideHeading />
        </AffilplatformPage>
      ) : route.childId === 'ap-mailing' ? (
        <MailingDashboardPage space="affilplatform" eyebrow="MAILING" />
      ) : route.childId === 'ap-mailing-kampane' ? (
        <MailingKampanePage space="affilplatform" eyebrow="MAILING" countries={AP_MAILING_COUNTRIES} />
      ) : route.childId === 'ap-mailing-skupiny' ? (
        <AffilmasterMailingSegmentyPage space="affilplatform" />
      ) : route.childId === 'ap-mailing-sablony' ? (
        <MailingSablonyPage space="affilplatform" eyebrow="MAILING" countries={AP_MAILING_COUNTRIES} />
      ) : route.childId === 'ap-vystupy-podpora' ? (
        <AffilplatformPage eyebrow="AFFILPLATFORM VÝSTUPY" title="Podpora" description="Obsah webu podpory support.affilplatform.com" actionsSlotId="stranky-podpora-actions">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyPodporaPage detail={route.detail} onOpenDetail={(d) => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId, detail: d }))} />
          </Suspense>
        </AffilplatformPage>
      ) : route.childId === 'ap-vystupy-b2b' ? (
        <AffilplatformPage eyebrow="AFFILPLATFORM VÝSTUPY" title="B2B" description="Nabídky, reporty a návody pro klienty, sdílené odkazem bez hesla">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyB2BPage space="affilplatform" tab={route.detail} onOpenTab={(d) => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId, detail: d }))} />
          </Suspense>
        </AffilplatformPage>
      ) : route.childId === 'ap-vystupy-vizitky' ? (
        <AffilplatformPage eyebrow="AFFILPLATFORM VÝSTUPY" title="Vizitky" description="Digitální vizitky členů týmu">
          <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
            <StrankyVizitkyPage />
          </Suspense>
        </AffilplatformPage>
      ) : (
        <ComingSoon label={pageTitle} />
      )}
    </PlatformShell>
  );
}

function AffilplatformDashboard({ onOpenLeady }: { onOpenLeady: () => void }) {
  return (
    <SpaceDashboard
      eyebrow="AFFILPLATFORM"
      description="Leady, jejich stav a další krok na jednom klidném místě"
      tiles={[
        { title: 'Otevřít CRM', description: 'Seznam leadů a jejich aktuální stav', action: { label: 'Přejít na leady', onClick: onOpenLeady } },
        { title: 'Začni u nových leadů', description: 'Otevři kontakt, doplň hodnotu a posuň jej do další fáze pipeline.' },
      ]}
    />
  );
}

function AffilplatformPage({ title, description, actionsSlotId, children, eyebrow = 'AFFILPLATFORM CRM' }: { title: string; description: string; actionsSlotId?: string; children: JSX.Element; eyebrow?: string }) {
  return (
    <ArcPage as="div" className="am-crm-wrap">
      <ArcPageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        meta={actionsSlotId ? <div id={actionsSlotId} className="crm-head-actions-slot" /> : undefined}
      />
      {children}
    </ArcPage>
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <ArcPage>
      <ArcPageHeader eyebrow="AFFILPLATFORM" title={label} />
      <ArcPanel><p>Tato stránka se připravuje.</p></ArcPanel>
    </ArcPage>
  );
}
